import ListCard from '@Components/ListCard/ListCard';
import DeleteTaxStatus from '@Components/Modals/TaxStatus/DeleteTaxStatus';
import TaxStatusModal from '@Components/Modals/TaxStatus/TaxStatusModal';
import TaxStatusServices from '@Services/TaxStatus.services.ts';
import { TaxStatus } from '@Types/Asset.types';
import { PaginatedResponse } from '@Types/Response.types';
import { PAGE_PROP, PAGE_SIZE, PAGINATED_DEFAULT, PATHS, SEARCH_PROP, TABLE_STRUCTURES } from '@Utils/constants';
import { handleRequestError } from '@Utils/request.utils';
import { Button, Icons, Pagination, SearchBox, SectionCard, useTopHat } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const TaxStatuses = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [taxStatus, setTaxStatus] = useState<PaginatedResponse<TaxStatus>>(PAGINATED_DEFAULT);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();
  const currentPage = Number(searchParams.get(PAGE_PROP) ?? '1');
  const searchQuery = searchParams.get(SEARCH_PROP) ?? '';
  const [showDeleteTaxModal, setShowDeleteTaxModal] = useState<boolean>(false);
  const [selectedTaxStatus, setSelectedTaxStatus] = useState<TaxStatus | undefined>(undefined);
  const [showNewTaxModal, setShowNewTaxModal] = useState<boolean>(false);

  useTopHat({
    activeNav: 'nav-settings',
    activeTab: undefined,
    tabs: [],
    breadcrumbs: [
      { title: 'Settings', href: PATHS.SETTINGS, id: 'Settings' },
      {
        title: 'Tax Status',
        href: `${PATHS.TAX_STATUS}?page=${1}`,
        id: 'Tax Status',
      },
    ],
  });

  const handleNewPage = (newPage: number, data: PaginatedResponse<TaxStatus>) => {
    if (newPage <= 0 || newPage > taxStatus.totalPages) {
      return;
    }
    const page = newPage > data.totalPages ? data.prevPage : newPage;
    if (currentPage !== page) {
      searchParams.set(PAGE_PROP, page?.toString() || '1');
      setSearchParams(searchParams);
    }
  };

  const getTaxes = () => {
    setSelectedTaxStatus(undefined);
    TaxStatusServices.searchTaxStatus({ search: searchQuery }, currentPage)
      .then(({ data }) => {
        setTaxStatus(data);
        setErrorMessage(undefined);
        handleNewPage(currentPage, data);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setIsLoading(true);
    getTaxes();
  }, [searchQuery, currentPage]);

  const handleSearchQueryChange = (newValue: string) => {
    searchParams.set(SEARCH_PROP, newValue);
    searchParams.set(PAGE_PROP, '1');
    if (!newValue) {
      searchParams.delete(SEARCH_PROP);
    }
    setSearchParams(searchParams);
  };

  const handleTaxAction = (id: string, setModal: (show: boolean) => void) => {
    setSelectedTaxStatus(taxStatus.docs.find((admin: TaxStatus) => admin.id === id));
    setModal(true);
  };

  return (
    <div className='grid'>
      <div className='col-12 col-md-3'>
        <SectionCard>
          <SearchBox
            id='search-tax-status'
            fullWidth
            focusOnRender
            value={searchQuery}
            defaultValue={searchQuery}
            onChange={handleSearchQueryChange}
          />
          <Button type='primary' className='button-with-icon' fullWidth onClick={() => setShowNewTaxModal(true)}>
            {Icons.Plus} Add new Tax Status
          </Button>
        </SectionCard>
      </div>
      <div className='col-12 col-md-9'>
        <ListCard<TaxStatus>
          title={'Tax Status'}
          structure={TABLE_STRUCTURES.TAX_STATUS}
          data={taxStatus.docs}
          loading={isLoading}
          withActions={{
            edit: (editId) => handleTaxAction(editId, setShowNewTaxModal),
            delete: (deleteId) => handleTaxAction(deleteId, setShowDeleteTaxModal),
          }}
          error={errorMessage}
          searchEntered={!!searchQuery}
        />
      </div>
      <div className='col-12 content-middle content-right-md'>
        <Pagination
          count={taxStatus.totalDocs}
          perPage={PAGE_SIZE}
          currentPage={currentPage - 1}
          onChange={(page) => handleNewPage(page + 1, taxStatus)}
        />
      </div>
      <TaxStatusModal
        show={showNewTaxModal}
        taxStatus={selectedTaxStatus}
        onClose={() => {
          setShowNewTaxModal(false);
          setSelectedTaxStatus(undefined);
        }}
        onSuccess={() => getTaxes()}
      />
      <DeleteTaxStatus
        show={showDeleteTaxModal}
        taxStatus={selectedTaxStatus}
        onClose={() => {
          setShowDeleteTaxModal(false);
          setSelectedTaxStatus(undefined);
        }}
        onSuccess={() => getTaxes()}
      />
    </div>
  );
};

export default TaxStatuses;
